import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Shared style contract for the square toolbar buttons (chat toggle, settings,
 * bug report, new chat, sign out). Single source of truth for the "active =
 * tinted accent" treatment so every toggle reads the same across the toolbar.
 *
 * - `tone` picks the accent color used for the active/hover treatment.
 * - `active` switches a toggle into its tinted, color-filled resting state.
 *
 * Each (tone, active) pair maps to exactly one compound variant so the emitted
 * classes never overlap/conflict — safe to use without an extra twMerge pass.
 */
export const toolbarButtonVariants = cva(
  [
    'h-7 w-7 flex items-center justify-center rounded-md',
    'transition-all duration-200 active:scale-95',
    // Consistent icon sizing for every toolbar button (16px, on the 4px grid).
    '[&_svg]:h-4 [&_svg]:w-4',
  ],
  {
    variants: {
      tone: {
        neutral: '',
        primary: '',
        danger: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Resting state — calm, muted, with a subtle hover tint.
      {
        tone: 'neutral',
        active: false,
        class: 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
      },
      {
        tone: 'primary',
        active: false,
        class: 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
      },
      // Sign-out: calm at rest, danger-coded red tint on hover (mirrors the
      // active toggle treatment, just in red).
      {
        tone: 'danger',
        active: false,
        class: 'text-muted-foreground hover:bg-danger/15 hover:text-danger',
      },
      // Active state — tinted accent background + tone-colored icon.
      {
        tone: 'neutral',
        active: true,
        class: 'bg-accent/50 text-foreground',
      },
      {
        tone: 'primary',
        active: true,
        class: 'bg-primary/15 text-primary hover:bg-primary/25',
      },
      {
        tone: 'danger',
        active: true,
        class: 'bg-danger/15 text-danger hover:bg-danger/25',
      },
    ],
    defaultVariants: {
      tone: 'neutral',
      active: false,
    },
  }
);

export type ToolbarButtonVariants = VariantProps<typeof toolbarButtonVariants>;
